import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

const cancelPhoto = (image) => {
  image?.removeAttribute('srcset');
  image?.removeAttribute('src');
};

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    try {
      this.#photoConfig = JSON.parse(this.querySelector('[data-pogi-variant-media]')?.textContent || 'null');
    } catch {
      this.#photoConfig = null;
    }

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    // The desktop PDP gallery can exceed a short viewport. Measure its natural
    // height so CSS can allow upward travel before pinning its lower controls.
    this.#stickySizeObserver?.disconnect();
    if (this.closest('.pogi-pdp-grid') && typeof ResizeObserver === 'function') {
      const updateStickySize = () => {
        const height = this.getBoundingClientRect().height;
        if (!this.isConnected || !Number.isFinite(height) || height <= 0) return;
        const value = `${height}px`;
        if (this.style.getPropertyValue('--pogi-gallery-height') !== value) {
          this.style.setProperty('--pogi-gallery-height', value);
        }
      };
      this.#stickySizeObserver = new ResizeObserver(updateStickySize);
      this.#stickySizeObserver.observe(this);
      updateStickySize();
    }
    if (this.#photoConfig) this.dispatchEvent(new CustomEvent('pogi:gallery-ready', { bubbles: true }));
  }

  #controller = new AbortController();
  #photoConfig = null;
  #stickySizeObserver = null;
  #photoContext = null;
  #photoCache = new Map();
  #photoGeneration = 0;
  #nativeGeneration = 0;
  #buyBoxReady = false;
  #photoPending = false;
  #photoTimer;
  #preview;
  #gatedState;
  #pendingGallery;
  #handoffImage;

  async playVideo(index, event) {
    if (!event?.isTrusted || document.hidden) return;
    const slideshow = this.refs.slideshow;
    const slide = slideshow?.slides?.[index];
    const media = slide?.querySelector('deferred-media');
    if (!media) return;
    const generation = this.#nativeGeneration;

    await slideshow.select(index, event, { animate: false });
    await customElements.whenDefined('deferred-media');
    if (!this.isConnected || document.hidden || generation !== this.#nativeGeneration) return;
    if (slideshow.slides?.[slideshow.current] !== slide || slide.getAttribute('aria-hidden') === 'true') return;

    if (media.getAttribute('data-media-loaded')) media.playMedia();
    else media.loadContent(false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#stickySizeObserver?.disconnect();
    this.#stickySizeObserver = null;
    this.#nativeGeneration++;
    this.#clearPreview();
    this.#pendingGallery = null;
    cancelPhoto(this.#handoffImage);
    this.#handoffImage = null;
    for (const entry of this.#photoCache.values()) cancelPhoto(entry.image);
    this.#photoCache.clear();
    this.#buyBoxReady = false;
  }

  #photoSelection(context) {
    if (!this.#photoConfig || this.presentation !== 'carousel' || this.refs.zoomDialogComponent) return null;
    if (String(context.productId) !== String(this.#photoConfig.productId)) return null;
    const mediaId = String(context.mediaId ?? this.#photoConfig.fallbackMediaId ?? '');
    if (!this.#photoConfig.media?.[mediaId]) return null;
    return { ...context, productId: String(context.productId), variantId: String(context.variantId), mediaId };
  }

  /** Begin only after the current hero and the purchasing controls are ready. */
  prepareVariantMedia(context) {
    const selection = this.#photoSelection(context);
    if (!selection) return;
    this.#photoContext = selection;
    this.#buyBoxReady = true;
    if (this.#photoPending) return;
    const hero = this.querySelector('slideshow-slide .product-media__image');
    if (!(hero instanceof HTMLImageElement) || String(hero.closest('[data-media-id]')?.dataset.mediaId) !== selection.mediaId) return;
    const generation = this.#photoGeneration;
    hero.decode().then(() => {
      if (this.isConnected && generation === this.#photoGeneration && !this.#photoPending) this.#warmPhotos(selection);
    }).catch(() => {});
  }

  /** Exact validated variant intent, before the unchanged native HTML request. */
  previewVariantMedia(context) {
    this.#nativeGeneration++;
    cancelPhoto(this.#handoffImage);
    this.#handoffImage = null;
    const selection = this.#photoSelection(context);
    if (!selection) {
      this.#clearPreview();
      return;
    }
    this.#photoContext = selection;
    this.#photoPending = true;
    const generation = ++this.#photoGeneration;
    clearTimeout(this.#photoTimer);
    this.#photoTimer = setTimeout(() => {
      if (generation !== this.#photoGeneration) return;
      const pending = this.#pendingGallery;
      this.#clearPreview();
      if (pending?.generation === this.#nativeGeneration && this.isConnected) this.replaceWith(pending.gallery);
    }, 5000);
    const entry = this.#loadPhoto(selection.mediaId, 'high');
    if (!entry) {
      this.#clearPreview();
      return;
    }
    entry.ready.then(() => {
      if (!this.isConnected || generation !== this.#photoGeneration) return;
      this.#showPreview(entry.image, selection.mediaId);
      this.#warmPhotos(selection);
    }).catch(() => {
      if (generation === this.#photoGeneration) this.#clearPreview();
    });
  }

  #loadPhoto(mediaId, priority) {
    const id = String(mediaId ?? this.#photoConfig?.fallbackMediaId ?? '');
    const record = this.#photoConfig?.media?.[id];
    if (!record) return null;
    let entry = this.#photoCache.get(id);
    if (entry) {
      if (priority === 'high') entry.image.fetchPriority = 'high';
      this.#photoCache.delete(id);
      this.#photoCache.set(id, entry);
      return entry;
    }
    // Retain at most a visible photo, its pending successor and two warm photos.
    while (this.#photoCache.size >= 4) {
      const oldest = [...this.#photoCache].find(([, cached]) => !cached.image.isConnected);
      if (!oldest) return null;
      this.#photoCache.delete(oldest[0]);
      cancelPhoto(oldest[1].image);
    }
    try {
      const source = new URL(record.src, document.baseURI);
      const image = new Image();
      image.className = 'product-media__image';
      image.alt = record.alt || '';
      image.width = record.width;
      image.height = record.height;
      image.decoding = 'async';
      image.loading = 'eager';
      image.fetchPriority = priority;
      image.sizes = this.#photoConfig.sizes;
      // Match the complete explicit Shopify srcset, even above intrinsic width.
      image.srcset = String(this.#photoConfig.widths).split(',').map((value) => {
        const width = Number(value.trim());
        const url = new URL(source);
        url.searchParams.set('width', String(width));
        return `${url.href} ${width}w`;
      }).join(', ');
      image.src = source.href;
      entry = { image, ready: image.decode() };
      entry.ready.catch(() => {
        if (this.#photoCache.get(id) === entry) this.#photoCache.delete(id);
      });
      this.#photoCache.set(id, entry);
      return entry;
    } catch {
      return null;
    }
  }

  #warmPhotos(selection) {
    const connection = navigator.connection;
    if (!this.#buyBoxReady || !this.isConnected || this.#photoContext?.variantId !== selection.variantId) return;
    if (connection?.saveData || /^(slow-2g|2g|3g)$/.test(connection?.effectiveType || '')) return;
    const seen = new Set([selection.mediaId]);
    for (const value of (selection.alternativeMediaIds || []).slice(0, 2)) {
      const id = String(value ?? this.#photoConfig.fallbackMediaId ?? '');
      if (seen.has(id)) continue;
      seen.add(id);
      this.#loadPhoto(id, 'low');
    }
  }

  #showPreview(image, mediaId) {
    const slideshow = this.refs.slideshow;
    const frame = slideshow?.querySelector('slideshow-slides > slideshow-slide');
    const viewport = slideshow?.querySelector('slideshow-container');
    if (!frame || !viewport) return;
    const preview = frame.cloneNode(true);
    for (const element of [preview, ...preview.querySelectorAll('*')]) {
      for (const { name } of [...element.attributes]) {
        if (name === 'id' || name === 'ref' || name.startsWith('on:')) element.removeAttribute(name);
      }
    }
    preview.removeAttribute('aria-hidden');
    preview.removeAttribute('hidden');
    preview.setAttribute('data-pogi-variant-preview', '');
    preview.dataset.mediaId = mediaId;
    preview.style.setProperty('position', 'absolute', 'important');
    preview.style.setProperty('inset', '0');
    preview.style.setProperty('height', '100%', 'important');
    preview.style.setProperty('z-index', '4');
    preview.style.setProperty('content-visibility', 'visible');
    preview.querySelector('.product-media__image')?.replaceWith(image);
    const innerMedia = preview.querySelector('.product-media');
    if (innerMedia) {
      innerMedia.dataset.mediaId = mediaId;
      innerMedia.style.setProperty('--ratio', String(image.width / image.height));
    }
    if (!this.#gatedState) {
      const scroller = slideshow.querySelector('slideshow-slides');
      this.#gatedState = {
        slideshow, inert: slideshow.hasAttribute('inert'), busy: this.getAttribute('aria-busy'), scroller,
        overflow: scroller?.style.getPropertyValue('overflow'), overflowPriority: scroller?.style.getPropertyPriority('overflow'),
      };
      slideshow.setAttribute('inert', '');
      scroller?.style.setProperty('overflow', 'hidden');
      this.setAttribute('aria-busy', 'true');
    }
    if (this.#preview) this.#preview.replaceWith(preview);
    else viewport.append(preview);
    this.#preview = preview;
  }

  #clearPreview() {
    this.#photoGeneration++;
    this.#photoPending = false;
    clearTimeout(this.#photoTimer);
    this.#preview?.remove();
    this.#preview = null;
    if (this.#gatedState) {
      const state = this.#gatedState;
      if (!state.inert) state.slideshow.removeAttribute('inert');
      if (state.busy === null) this.removeAttribute('aria-busy');
      else this.setAttribute('aria-busy', state.busy);
      if (state.overflow) state.scroller?.style.setProperty('overflow', state.overflow, state.overflowPriority);
      else state.scroller?.style.removeProperty('overflow');
      this.#gatedState = null;
    }
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const generation = ++this.#nativeGeneration;
    cancelPhoto(this.#handoffImage);
    this.#handoffImage = null;
    this.#pendingGallery = null;
    if (event.detail?.variantId && String(event.detail.variantId) !== this.#photoContext?.variantId) this.#clearPreview();

    event.promise
      .then(async ({ detail }) => {
        if (generation !== this.#nativeGeneration || !this.isConnected) return;

        const newMediaGallery = detail?.html?.querySelector('media-gallery');
        if (!newMediaGallery) {
          this.#clearPreview();
          return;
        }
        this.#pendingGallery = { gallery: newMediaGallery, generation };
        const hero = newMediaGallery.querySelector('slideshow-slide .product-media__image');
        if (this.#photoPending && hero instanceof HTMLImageElement && String(hero.closest('[data-media-id]')?.dataset.mediaId) === this.#photoContext?.mediaId) {
          // Decode the authoritative image in this document before removing the
          // correct preview. Matching srcset/sizes reuse the same in-flight URL.
          const image = new Image();
          for (const { name, value } of [...hero.attributes]) {
            if (!['src', 'srcset', 'sizes'].includes(name)) image.setAttribute(name, value);
          }
          image.sizes = hero.getAttribute('sizes') || '';
          image.srcset = hero.getAttribute('srcset') || '';
          image.src = hero.getAttribute('src') || '';
          this.#handoffImage = image;
          try {
            await image.decode();
            if (generation !== this.#nativeGeneration || !this.isConnected) return;
            hero.replaceWith(image);
          } catch {
            // A failed image falls back to the unchanged server gallery.
          }
          if (this.#handoffImage === image) this.#handoffImage = null;
        }
        if (generation !== this.#nativeGeneration || !this.isConnected) return;

        this.#pendingGallery = null;
        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (generation !== this.#nativeGeneration || !this.isConnected) return;
        this.#clearPreview();
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
